<script setup>
import { computed, ref, unref, watch } from 'vue'
import { getIcon } from '@/tiptap/constants/icons'
import { useLocale } from '@/tiptap/locales'

import ImageUpload from './ImageUpload.vue'
import ImageUrl from './ImageUrl.vue'

const props = defineProps({
  value: { type: Object, default: () => ({}) },
  editor: { type: Object, required: true },
  upload: { type: Function, default: undefined },
  imageTabs: { type: Array, default: () => [] },
  hiddenTabs: { type: Array, default: () => [] },
  destroy: { type: Function, default: undefined }
})

const { t } = useLocale()

const dialog = ref(false)
const tab = ref(0)
const form = ref({})

const defaultImageTabs = computed(() => {
  const defTabs = [
    {
      name: unref(t)('editor.image.dialog.tab.upload'),
      type: 'upload',
      component: ImageUpload
    },
    {
      name: unref(t)('editor.image.dialog.tab.url'),
      type: 'url',
      component: ImageUrl
    }
  ]

  const filterDefTabs = defTabs.filter(item => {
    if (!item.type) return item
    return !props.hiddenTabs.includes(item.type)
  })

  return [...filterDefTabs, ...props.imageTabs]
})

const disabledApply = computed(() => {
  const { src } = unref(form)
  if (typeof src === 'string' && src !== '') return false
  return true
})

async function apply() {
  const { src, lockAspectRatio, height } = unref(form)
  if (!src) return

  props.editor
    .chain()
    .focus()
    .setImage({
      ...unref(form),
      src,
      height: lockAspectRatio ? undefined : height
    })
    .run()

  close()
}

function close() {
  dialog.value = false
  form.value = {}

  setTimeout(() => props.destroy && props.destroy(), 300)
}

watch(
  () => props.value,
  val => {
    form.value = {
      ...unref(form),
      ...val
    }
  },
  { immediate: true, deep: true }
)
</script>

<template>
  <VDialog v-model="dialog" max-width="400" activator="parent" @click:outside="close">
    <VCard>
      <VToolbar class="px-6" density="compact">
        <span class="headline">{{ t('editor.image.dialog.title') }}</span>

        <VSpacer />

        <VBtn class="mx-0" icon @click="close">
          <VIcon :icon="getIcon('close')" />
        </VBtn>
      </VToolbar>

      <VTabs v-model="tab">
        <VTab v-for="(imageTab, i) in defaultImageTabs" :key="i" :value="i">
          {{ imageTab.name }}
        </VTab>
      </VTabs>

      <VCardText>
        <VWindow v-model="tab">
          <VWindowItem v-for="(imageTab, i) in defaultImageTabs" :key="i" :value="i">
            <component :is="imageTab.component" v-model="form" :upload="upload" :t="t" />
          </VWindowItem>
        </VWindow>
      </VCardText>

      <VCardActions>
        <VBtn :disabled="disabledApply" @click="apply">
          {{ t('editor.image.dialog.button.apply') }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>